import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import { useGetAdminProfileDetails } from '../../../api-refactored/user/useGetAdminProfileDetails';
import { usePutAdminProfileDetails } from '../../../api-refactored/user/usePutAdminProfileDetails';
import { UpdateAdminProfileDetailsRequest } from '../../../api/user/model';
import { AdminProfileFormData, AdminProfileFormDefaultValues } from '../../../models';

interface UseAdminProfileDetailsReturn {
  formDefaultValues: AdminProfileFormDefaultValues | null;
  updateAdminDetailsApiStatus: MutationStatus;
  updateAdminDetails: (formData: AdminProfileFormData) => void;
  updateAdminDetailsError: string;
  refetch: any;
}

/**
 * A hook used to get and update candidate profile details.
 */
export const useAdminProfileDetails = (): UseAdminProfileDetailsReturn => {
  const { data, refetch } = useGetAdminProfileDetails();
  const { t } = useTranslation();
  const [updateProfileError, setUpdateProfileError] = useState('');
  const formDefaultValues: AdminProfileFormDefaultValues | null = useMemo(() => {
    if (!data) {
      return null;
    }
    return {
      statutoryName: data.statutoryName,
      userIdType: data.userIdType,
      adminId: data.adminId,
      dateOfBirth: data.dateOfBirth,
      email: data.email,
      accountType: data?.accountType,
      photoUUid: data.photoUUid,
      photoUrl: data.photoUrl,
      // contact numbers
      mobileCountryCode: data?.mobileCountryCode,
      mobileContactNumber: data?.mobileContactNumber,
    };
  }, [data]);

  const { mutate, status } = usePutAdminProfileDetails({
    onSuccess: () => {
      setUpdateProfileError('');
    },
    onError: () => {
      setUpdateProfileError(t('profile->>CandidateProfileDetail.SubmitErrorMsg'));
    },
  });

  const updateAdminDetails = (formData: AdminProfileFormData) => {
    const putRequestBody: UpdateAdminProfileDetailsRequest = {
      statutoryName: formData.statutoryName || data?.statutoryName,
      userIdType: formData.userIdType || data?.userIdType,
      email: formData.email || data?.email,
      photoUuid: formData.photoUUid === '' ? undefined : formData.photoUUid,
      dateOfBirth: formData.dateOfBirth || data?.dateOfBirth,

      // contact numbers
      mobileNumber: {
        countryCode: formData.mobileCountryCode,
        number: formData.mobileContactNumber,
      },
      adminId: formData.adminId || data?.adminId,
      accountType: formData.accountType || data?.accountType,
    };
    mutate(putRequestBody);
  };

  return {
    formDefaultValues,
    updateAdminDetailsApiStatus: status,
    updateAdminDetails,
    updateAdminDetailsError: updateProfileError,
    refetch,
  };
};
